import React from 'react';

export type BadgeVariant = 'default' | 'primary' | 'success' | 'warning' | 'error' | 'info' | 'neutral';
export type BadgeSize = 'sm' | 'md';

export interface BadgeProps extends React.HTMLAttributes<HTMLSpanElement> {
  variant?: BadgeVariant;
  size?: BadgeSize;
  dot?: boolean;
}

const badgeVariantStyles: Record<BadgeVariant, { bg: string; text: string; border: string; dotColor: string }> = {
  default: {
    bg: 'bg-[#F7FAFA]',
    text: 'text-[#111111]',
    border: 'border-[#E5E9E9]',
    dotColor: 'bg-[#5F6368]',
  },
  primary: {
    bg: 'bg-[#E8FAF9]',
    text: 'text-[#1b8e88]',
    border: 'border-[#56E8E1]/40',
    dotColor: 'bg-[#1fb4ac]',
  },
  success: {
    bg: 'bg-[#EAF8F1]',
    text: 'text-[#20A66A]',
    border: 'border-[#20A66A]/30',
    dotColor: 'bg-[#20A66A]',
  },
  warning: {
    bg: 'bg-[#FFFDF5]',
    text: 'text-[#B88A18]',
    border: 'border-[#D4A923]/40',
    dotColor: 'bg-[#D4A923]',
  },
  error: {
    bg: 'bg-[#FDEDEF]',
    text: 'text-[#D94A5C]',
    border: 'border-[#D94A5C]/30',
    dotColor: 'bg-[#D94A5C]',
  },
  info: {
    bg: 'bg-blue-50',
    text: 'text-blue-700',
    border: 'border-blue-200',
    dotColor: 'bg-blue-500',
  },
  neutral: {
    bg: 'bg-gray-100',
    text: 'text-gray-700',
    border: 'border-gray-200',
    dotColor: 'bg-gray-500',
  },
};

const badgeSizeStyles: Record<BadgeSize, string> = {
  sm: 'px-2 py-0.5 text-[11px] font-semibold rounded-md gap-1.5',
  md: 'px-2.5 py-1 text-xs font-bold rounded-lg gap-2',
};

export const Badge: React.FC<BadgeProps> = ({
  variant = 'default',
  size = 'md',
  dot = false,
  className = '',
  children,
  ...props
}) => {
  const style = badgeVariantStyles[variant];

  return (
    <span
      className={`inline-flex items-center border font-medium uppercase tracking-wider ${style.bg} ${style.text} ${style.border} ${badgeSizeStyles[size]} ${className}`}
      {...props}
    >
      {dot && <span className={`w-1.5 h-1.5 rounded-full shrink-0 ${style.dotColor}`} />}
      {children}
    </span>
  );
};
